import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

interface FormatEventDateTimeOptions {
  startsAt: string;
  endsAt?: string;
  showStartTime?: boolean;
  showEndTime?: boolean;
  timeZone?: string;
  timeFormat?: '12_hour' | '24_hour' | 'military';
  long?: boolean;
}

/**
 * Formats event date/time according to V1 event settings
 * Mirrors the Ruby display_date_time_range_with_zone helper
 */
export function formatEventDateTime({
  startsAt,
  endsAt,
  showStartTime = true,
  showEndTime = true,
  timeZone = 'America/New_York',
  timeFormat = '12_hour',
  long = true
}: FormatEventDateTimeOptions): string {
  if (!startsAt) return '';

  try {
    const startDate = parseISO(startsAt);
    const endDate = endsAt ? parseISO(endsAt) : startDate;

    // Check if dates are valid
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return '';
    }

  // If not showing any times, just show date range
  if (!showStartTime && !showEndTime) {
    return formatDateRange(startDate, endDate, long);
  }

  // Check if same day
  const isSameDay = format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd');

  // If start and end are exactly the same time
  if (format(startDate, 'yyyy-MM-dd HH:mm') === format(endDate, 'yyyy-MM-dd HH:mm')) {
    return formatDayWithTime(startDate, timeZone, timeFormat, long, showStartTime || showEndTime);
  }

  // Same day, different times
  if (isSameDay) {
    return formatDayWithTimeRange(startDate, endDate, timeZone, timeFormat, long, showStartTime, showEndTime);
  }

  // Different days
  return formatDaysWithTimeRange(startDate, endDate, timeZone, timeFormat, long, showStartTime, showEndTime);
  } catch (error) {
    return '';
  }
}

function formatDateRange(startDate: Date, endDate: Date, long: boolean): string {
  // Match Ruby strftime: %A = EEEE, %B = MMMM, %-e = d (no padding)
  const dateFormat = long ? 'EEEE, MMMM d, yyyy' : 'EEE, MMM d, yyyy';
  
  const startStr = format(startDate, dateFormat);
  const endStr = format(endDate, dateFormat);

  if (startStr === endStr) {
    return startStr;
  }

  return `${startStr} — ${endStr}`;
}

function formatDayWithTime(
  date: Date,
  timeZone: string,
  timeFormat: string,
  long: boolean,
  showTime: boolean
): string {
  const dateFormat = long ? 'EEEE, MMMM d, yyyy' : 'EEE, MMM d, yyyy';
  let result = '';
  
  try {
    result = formatInTimeZone(date, timeZone, dateFormat);
  } catch (e) {
    // Fallback if timezone is invalid
    result = format(date, dateFormat);
  }

  if (showTime) {
    const timeStr = formatTime(date, timeZone, timeFormat);
    let tzStr = 'EDT'; // Default
    try {
      tzStr = formatInTimeZone(date, timeZone, 'zzz');
    } catch (e) {
      // Use default
    }
    result += `, ${timeStr} ${tzStr}`;
  }

  return result;
}

function formatDayWithTimeRange(
  startDate: Date,
  endDate: Date,
  timeZone: string,
  timeFormat: string,
  long: boolean,
  showStartTime: boolean,
  showEndTime: boolean
): string {
  const dateFormat = long ? 'EEEE, MMMM d, yyyy' : 'EEE, MMM d, yyyy';
  let result = '';
  
  try {
    result = formatInTimeZone(startDate, timeZone, dateFormat);
  } catch (e) {
    // Fallback if timezone is invalid
    result = format(startDate, dateFormat);
  }

  if (timeFormat === '12_hour') {
    let startMeridian, endMeridian, tzStr;
    
    try {
      startMeridian = formatInTimeZone(startDate, timeZone, 'a');
      endMeridian = formatInTimeZone(endDate, timeZone, 'a');
      tzStr = formatInTimeZone(startDate, timeZone, 'zzz');
    } catch (e) {
      // Fallback if timezone is invalid
      startMeridian = format(startDate, 'a');
      endMeridian = format(endDate, 'a');
      tzStr = 'EDT'; // Default to EDT for now
    }

    if (startMeridian === endMeridian) {
      // Same AM/PM - "July 5, 2025, 12:00 - 1:00 PM EST"
      if (showStartTime) {
        try {
          result += `, ${formatInTimeZone(startDate, timeZone, 'h:mm')}`;
        } catch (e) {
          result += `, ${format(startDate, 'h:mm')}`;
        }
      }
      if (showStartTime && showEndTime) {
        result += ' —';
      }
      if (showEndTime) {
        try {
          result += ` ${formatInTimeZone(endDate, timeZone, 'h:mm')}`;
        } catch (e) {
          result += ` ${format(endDate, 'h:mm')}`;
        }
      }
      if (showStartTime || showEndTime) {
        result += ` ${endMeridian} ${tzStr}`;
      }
    } else {
      // Different AM/PM - "July 5, 2025, 11:00 AM — 1:00 PM EST"
      if (showStartTime) {
        try {
          result += `, ${formatInTimeZone(startDate, timeZone, 'h:mm a')}`;
        } catch (e) {
          result += `, ${format(startDate, 'h:mm a')}`;
        }
      }
      if (showStartTime && showEndTime) {
        result += ' —';
      } else if (!showStartTime && showEndTime) {
        result += ',';
      }
      if (showEndTime) {
        try {
          result += ` ${formatInTimeZone(endDate, timeZone, 'h:mm a')}`;
        } catch (e) {
          result += ` ${format(endDate, 'h:mm a')}`;
        }
      }
      if (showStartTime || showEndTime) {
        result += ` ${tzStr}`;
      }
    }
  } else {
    // 24-hour format
    const tzStr = formatInTimeZone(startDate, timeZone, 'zzz');
    if (showStartTime) {
      result += `, ${formatInTimeZone(startDate, timeZone, 'HH:mm')}`;
    }
    if (showEndTime) {
      result += ` — ${formatInTimeZone(endDate, timeZone, 'HH:mm')}`;
    }
    if (showStartTime || showEndTime) {
      result += ` ${tzStr}`;
    }
  }

  return result;
}

function formatDaysWithTimeRange(
  startDate: Date,
  endDate: Date,
  timeZone: string,
  timeFormat: string,
  long: boolean,
  showStartTime: boolean,
  showEndTime: boolean
): string {
  const dateFormat = long ? 'EEEE, MMMM d, yyyy' : 'EEE, MMM d, yyyy';
  const startTz = formatInTimeZone(startDate, timeZone, 'zzz');
  const endTz = formatInTimeZone(endDate, timeZone, 'zzz');
  const switchZone = startTz !== endTz ? ` ${startTz}` : '';

  let result = formatInTimeZone(startDate, timeZone, dateFormat);

  if (timeFormat === '12_hour') {
    if (showStartTime) {
      result += `, ${formatInTimeZone(startDate, timeZone, 'h:mm a')}${switchZone}`;
      if (!showEndTime) {
        result += ` ${startTz}`;
      }
    }
    result += ` — ${formatInTimeZone(endDate, timeZone, dateFormat)}`;
    if (showEndTime) {
      result += `, ${formatInTimeZone(endDate, timeZone, 'h:mm a')} ${endTz}`;
    }
  } else {
    // 24-hour format
    if (showStartTime) {
      result += `, ${formatInTimeZone(startDate, timeZone, 'HH:mm')}${switchZone}`;
    }
    result += ` — ${formatInTimeZone(endDate, timeZone, dateFormat)}`;
    if (showEndTime) {
      result += `, ${formatInTimeZone(endDate, timeZone, 'HH:mm')} ${endTz}`;
    }
  }

  return result;
}

function formatTime(date: Date, timeZone: string, timeFormat: string): string {
  // Match Ruby formats: %-I:%M for 12-hour (no padding), %H:%M for 24-hour
  try {
    if (timeFormat === '12_hour') {
      return formatInTimeZone(date, timeZone, 'h:mm a');
    } else {
      // 24_hour and military both use HH:mm
      return formatInTimeZone(date, timeZone, 'HH:mm');
    }
  } catch (e) {
    // Fallback if timezone is invalid
    if (timeFormat === '12_hour') {
      return format(date, 'h:mm a');
    } else {
      return format(date, 'HH:mm');
    }
  }
}